/*
 * ConsoleDrawer — production shell v1 (Developer only)
 * -----------------------------------------------------------------------------
 * Role-scoped left-drawer navigation used by dashboards under the existing
 * Navbar. Extracted from /mockup/console-drawer (approved design), wired to:
 *   • React Router for nav (drawer items navigate to real routes)
 *   • Live /api/modules for the Course Content tree
 *   • Auto-scoped AI Tutor pane (bubble → dock → float), scope = active topic
 *
 * v1 rollout: Developer dashboard first. Other roles migrate one at a time.
 * Preserves the existing Navbar (patent strip, logo, avatar, logout) verbatim.
 * -----------------------------------------------------------------------------
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/apiClient';
import { useTutorScope } from '../context/TutorScopeContext';

const C = {
  navy: '#0E2A47', gold: '#FCD359', goldDark: '#E0B73D',
  ink: '#101828', dim: '#667085', rule: '#E4E7EC',
  bone: '#F7F3EA', bg: '#FAFAF7', panel: '#FFFFFF',
};

const STATUS = {
  Published:      { bg: 'rgba(22,163,74,0.14)',  fg: '#16A34A', border: 'rgba(22,163,74,0.35)' },
  Draft:          { bg: 'rgba(148,163,184,0.18)', fg: '#64748B', border: 'rgba(148,163,184,0.45)' },
  'Needs review': { bg: 'rgba(234,88,12,0.15)',   fg: '#EA580C', border: 'rgba(234,88,12,0.4)' },
};

// Role-scoped visual accent — a thin stripe at the top of the drawer + a
// matching underline on the console label. Makes the current mode instantly
// recognisable in demos and screenshots.
const ROLE_ACCENT = {
  developer: { color: '#7C3AED', label: 'DEVELOPER' },  // violet
  executive: { color: '#B45309', label: 'EXECUTIVE' },  // amber
  faculty:   { color: '#0891B2', label: 'FACULTY'   },  // cyan
  student:   { color: '#16A34A', label: 'STUDENT'   },  // green
};

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const KBD = isMac ? '⌘' : 'Ctrl';
const kbdStyle = { display: 'inline-block', padding: '1px 5px', border: '1px solid #2A4A6E', borderRadius: 3, fontSize: 10, fontFamily: 'ui-monospace, monospace', background: 'rgba(255,255,255,0.05)', color: '#E8EEF6' };

/* Role → nav sections. Each item is (label, icon, and either href or action). */
const ROLE_NAV = {
  developer: [
    { title: 'Class & Grading', items: [
      { icon: '📊', label: 'Executive',        href: '/executive',            hint: 'Cohort-level dashboard.' },
      { icon: '🎓', label: 'Gradebook',        href: '/gradebook',            hint: 'Composite %, weighted, curve.' },
      { icon: '🧭', label: 'Chat Efficiency',  href: '/chat-efficiency',      hint: 'Per-student engagement.' },
      { icon: '📊', label: 'Chat Analytics',   href: '/developer/analytics',  hint: 'Chat logs & scope.' },
      { icon: '🧠', label: 'Recall / Connect', href: '/study',                hint: '5-strategy spaced review.' },
      { icon: '📊', label: 'Recall Insights',  href: '/faculty/insights',     hint: 'Topic Health cohort view.' },
    ]},
    { title: 'Faculty Telescopes', kicker: 'Patent-aligned', items: [
      { icon: '🛤',  label: 'Pathways',      href: '/pathways',      hint: 'Socratic dialogue (Patent A).' },
      { icon: '🔍', label: 'Authenticity',   href: '/authenticity',  hint: 'Paste/revision/AI-write.' },
      { icon: '📧', label: 'Digest',         href: '/digest',        hint: 'Weekly authenticity email.' },
    ]},
    { title: 'Administration', items: [
      { icon: '👥', label: 'Users',          href: '/users',         hint: 'List, roles, delete.' },
      { icon: '🎒', label: 'Canvas LTI',     href: '/lti-config',    hint: 'LTI 1.3 endpoints.' },
      { icon: '🩺', label: 'Module Audit',   action: 'module-audit', hint: 'Scan for orphan topics / mismatched module IDs.' },
    ]},
    { title: 'Views & Resources', items: [
      { icon: '📥', label: 'Downloads',            href: '/downloads/',          external: true, hint: 'Patents, marketing artefacts.' },
    ]},
  ],

  faculty: [
    { title: 'Class & Grading', items: [
      { icon: '📊', label: 'Executive',           href: '/executive',        hint: 'Class-level metrics.' },
      { icon: '🎓', label: 'Gradebook',           href: '/gradebook',        hint: 'Composite %, weighted, curve.' },
      { icon: '📝', label: 'Rubrics & Grading',   href: '/rubrics',          hint: 'Rubrics + AI-assisted grading.' },
      { icon: '🧭', label: 'Chat Efficiency',     href: '/chat-efficiency',  hint: 'Per-student engagement.' },
      { icon: '🧠', label: 'Recall / Connect',    href: '/study',            hint: 'Try it as a student.' },
      { icon: '📊', label: 'Recall Insights',     href: '/faculty/insights', hint: 'Topic Health + at-risk cohort.' },
    ]},
    { title: 'Faculty Telescopes', kicker: 'Patent-aligned', items: [
      { icon: '🛤',  label: 'Pathways',      href: '/pathways',     hint: 'Socratic dialogue (Patent A).' },
      { icon: '🔍', label: 'Authenticity',   href: '/authenticity', hint: 'Paste/revision/AI-write.' },
      { icon: '📧', label: 'Digest',         href: '/digest',       hint: 'Weekly authenticity email.' },
    ]},
  ],

  student: [
    { title: 'Learning', items: [
      { icon: '🧠', label: 'Recall / Connect', href: '/study',              hint: 'Today\u2019s spaced session.' },
      { icon: '⚔️', label: 'Duels',            href: '/study/duels',        hint: 'Async peer + AI duels.' },
      { icon: '🕸',  label: 'Concept Map',      href: '/study/concept-map',  hint: 'How this week connects.' },
    ]},
    { title: 'My Progress', items: [
      { icon: '🎓', label: 'My Grades',        href: '/my-grades',        hint: 'Weighted composite so far.' },
      { icon: '📊', label: 'My Insights',      href: '/study/insights',   hint: 'Mastery, streak, duel record.' },
      { icon: '📝', label: 'Graded Work',      href: '/rubrics',          hint: 'AI-graded submissions.' },
    ]},
  ],

  executive: [
    { title: 'Cohort Story', items: [
      { icon: '📊', label: 'Executive',        href: '/executive',        hint: 'Board-ready cohort narrative.' },
      { icon: '🎓', label: 'Gradebook',        href: '/gradebook',        hint: 'Program-level composite.' },
      { icon: '📊', label: 'Recall Insights',  href: '/faculty/insights', hint: 'Topic Health rollup.' },
    ]},
    { title: 'Faculty Telescopes', kicker: 'Patent-aligned', items: [
      { icon: '🛤',  label: 'Pathways',      href: '/pathways',     hint: 'Trajectory patterns.' },
      { icon: '🔍', label: 'Authenticity',   href: '/authenticity', hint: 'Program-wide integrity.' },
      { icon: '📧', label: 'Digest',         href: '/digest',       hint: 'Weekly executive digest.' },
    ]},
  ],
};

const PIN_KEY = 'console.drawerPinned';

export default function ConsoleDrawer({ role = 'developer', actions = {}, children }) {
  const nav = useNavigate();
  const tutor = useTutorScope();
  const [pinned, setPinned] = useState(() => localStorage.getItem(PIN_KEY) !== '0');
  const [open, setOpen]     = useState(false);
  const [modules, setModules] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});
  const [activeTopic, setActiveTopic] = useState({ moduleId: null, topicId: null });
  const [searchQ, setSearchQ] = useState('');
  const searchRef = useRef(null);
  const sections = ROLE_NAV[role] || ROLE_NAV.developer;
  const modulePathBase = ({
    developer: '/developer/module/',
    faculty:   '/teacher/module/',
    student:   '/student/module/',
    executive: '/executive',              // executive uses filters, not per-module routes
  })[role] || '/developer/module/';

  useEffect(() => { localStorage.setItem(PIN_KEY, pinned ? '1' : '0'); }, [pinned]);

  // Load modules (authenticated — user is already logged in on the dashboard)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/modules');
        const shaped = (data || []).map((m) => ({
          id: m.id,
          code: `M${m.order ?? '?'}`,
          name: cleanTitle(m.title),
          status: normStatus(m.status),
          topics: (m.topics || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map((t) => ({ id: t.id, name: t.title || '(untitled)' })),
        }));
        setModules(shaped);
        if (shaped[0]) setExpandedModules({ [shaped[0].id]: true });
      } catch (_) { /* stay empty */ }
    })();
  }, []);

  // Keyboard shortcuts (Ctrl/Cmd+J is owned by AITutorLauncher itself)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault(); setOpen(true); setTimeout(() => searchRef.current?.focus(), 60); return;
      }
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) return;
      if (e.key === '[') { e.preventDefault(); setPinned((p) => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const askable = searchQ.trim();
  const isQuestion = askable.length > 40 || askable.includes('?');

  const filterResults = useMemo(() => {
    const q = askable.toLowerCase(); if (!q || isQuestion) return null;
    const navMatch = sections.flatMap((s) => s.items.filter((it) => it.label.toLowerCase().includes(q)).map((it) => ({ kind: 'nav', ...it, section: s.title })));
    const modMatch = modules.filter((m) => m.name.toLowerCase().includes(q)).map((m) => ({ kind: 'module', id: m.id, label: `${m.code} · ${m.name}` }));
    const topMatch = modules.flatMap((m) => m.topics.filter((t) => t.name.toLowerCase().includes(q)).map((t) => ({ kind: 'topic', moduleId: m.id, topicId: t.id, label: `${m.code} → ${t.name}` })));
    return [...navMatch, ...modMatch, ...topMatch].slice(0, 10);
  }, [askable, isQuestion, sections, modules]);

  const currentModule = modules.find((m) => m.id === activeTopic.moduleId);
  const currentTopic  = currentModule?.topics.find((t) => t.id === activeTopic.topicId);

  // Push tree selection up to the app-level tutor scope so the single
  // AITutorLauncher instance sees "the module/topic the user is on".
  useEffect(() => {
    tutor.setScope({ module: currentModule || null, topic: currentTopic || null });
  }, [currentModule, currentTopic, tutor]);

  const askTutor = useCallback((q) => {
    if (!q?.trim()) return;
    tutor.pushAsk(q);      // seeds the convo + auto-opens docked pane
    setSearchQ('');
  }, [tutor]);

  const drawerOpen = pinned || open;

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 'calc(100vh - 60px)', position: 'relative', background: C.bg }}>
      {/* Hamburger stays outside drawer as a fixed top-left toggle */}
      <button
        onClick={() => setPinned((p) => !p)}
        title={pinned ? 'Unpin drawer  ([)' : 'Pin drawer  ([)'}
        data-testid="console-hamburger"
        style={{
          position: 'fixed', top: 74, left: 12, zIndex: 46,
          width: 36, height: 36, borderRadius: 6, border: `1px solid ${C.rule}`,
          background: drawerOpen ? C.navy : C.panel, color: drawerOpen ? C.gold : C.navy,
          cursor: 'pointer', display: 'grid', placeItems: 'center', boxShadow: '0 2px 6px rgba(16,24,40,0.08)',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <rect x="3" y="4.5" width="14" height="1.8" rx="0.9" fill="currentColor"/>
          <rect x="3" y="9.1" width="14" height="1.8" rx="0.9" fill="currentColor"/>
          <rect x="3" y="13.7" width="14" height="1.8" rx="0.9" fill="currentColor"/>
        </svg>
      </button>

      <Drawer
        role={role}
        visible={drawerOpen} pinned={pinned}
        onPinToggle={() => setPinned((p) => !p)}
        sections={sections}
        modules={modules}
        expandedModules={expandedModules}
        setExpandedModules={setExpandedModules}
        activeTopic={activeTopic}
        setActiveTopic={(sel) => {
          setActiveTopic(sel); if (!pinned) setOpen(false);
          const mid = sel.moduleId; if (!mid) return;
          if (role === 'executive') nav('/executive');            // exec doesn't have per-module pages
          else nav(`${modulePathBase}${mid}`);
        }}
        searchQ={searchQ} setSearchQ={setSearchQ}
        searchRef={searchRef}
        results={filterResults} askable={askable} isQuestion={isQuestion}
        onNav={(item) => {
          if (!pinned) setOpen(false);
          if (item.action && typeof actions[item.action] === 'function') { actions[item.action](); return; }
          if (item.external) { window.open(item.href, '_blank'); return; }
          if (item.href) nav(item.href);
        }}
        onAsk={askTutor}
      />

      <main data-testid="console-workspace" style={{ flex: 1, minWidth: 0 }}>
        {children}
      </main>

      {!pinned && open && (
        <div onClick={() => setOpen(false)}
             style={{ position: 'fixed', inset: '60px 0 0 300px', background: 'rgba(14,42,71,0.28)', backdropFilter: 'blur(2px)', zIndex: 40 }} />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Drawer
 * ────────────────────────────────────────────────────────── */
function Drawer(props) {
  const { visible, pinned, onPinToggle, sections, modules,
          expandedModules, setExpandedModules, activeTopic, setActiveTopic,
          searchQ, setSearchQ, searchRef, results, askable, isQuestion,
          onNav, onAsk } = props;
  // role identity is now shown in the top-nav view switcher, not in the drawer
  return (
    <aside
      data-testid="console-drawer"
      style={{
        width: visible ? 300 : 0, minWidth: visible ? 300 : 0,
        transition: 'width 200ms ease, min-width 200ms ease',
        background: C.navy, color: '#E8EEF6', overflow: 'hidden',
        position: pinned ? 'sticky' : 'fixed',
        top: pinned ? 60 : 60, bottom: pinned ? 'auto' : 0,
        alignSelf: 'flex-start', height: pinned ? 'calc(100vh - 60px)' : 'calc(100vh - 60px)',
        zIndex: 45, boxShadow: pinned ? 'none' : '4px 0 24px rgba(14,42,71,0.35)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#9FB2CB', fontWeight: 700 }}>Navigation</span>
          <div style={{ flex: 1 }} />
          <button onClick={onPinToggle} data-testid="drawer-pin-toggle"
            title={pinned ? 'Unpin drawer' : 'Pin drawer'}
            style={{ background: 'transparent', border: `1px solid ${pinned ? C.gold : '#2A4A6E'}`, color: pinned ? C.gold : '#9FB2CB', fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em' }}>
            {pinned ? 'PINNED' : 'PIN'}
          </button>
        </div>
        <div style={{ position: 'relative' }}>
          <input ref={searchRef} value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && askable) onAsk(askable); if (e.key === 'Escape') setSearchQ(''); }}
            placeholder="Ask the tutor, or search modules & tools…"
            data-testid="drawer-search-input"
            style={{
              width: '100%', padding: '9px 10px 9px 30px',
              background: isQuestion ? 'rgba(252,211,89,0.10)' : 'rgba(255,255,255,0.06)',
              border: `1px solid ${isQuestion ? 'rgba(252,211,89,0.35)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 8, color: '#E8EEF6', fontSize: 13, outline: 'none',
            }} />
          <span style={{ position: 'absolute', left: 9, top: 8, fontSize: 13, opacity: 0.7 }}>{isQuestion ? '💬' : '🔎'}</span>
          <span style={{ position: 'absolute', right: 6, top: 8, fontSize: 10, color: '#9FB2CB', border: '1px solid #2A4A6E', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace' }}>{KBD}K</span>
        </div>
        {askable && isQuestion && (
          <button onClick={() => onAsk(askable)} data-testid="ask-tutor-cta"
            style={{ width: '100%', marginTop: 6, padding: '7px 10px', borderRadius: 6, cursor: 'pointer', border: `1px solid ${C.goldDark}`, background: 'rgba(252,211,89,0.14)', color: C.gold, textAlign: 'left', fontSize: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>💬</span><span style={{ flex: 1 }}>Ask AI Tutor ⏎</span>
          </button>
        )}
      </div>

      <div style={{ padding: '10px 8px 20px', overflowY: 'auto', flex: 1 }}>
        {results && (
          <div style={{ marginBottom: 12, background: 'rgba(252,211,89,0.06)', border: '1px solid rgba(252,211,89,0.15)', borderRadius: 6, padding: 6 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, padding: '4px 8px' }}>{results.length} result{results.length === 1 ? '' : 's'}</div>
            {results.length === 0 && <div style={{ padding: '6px 8px', color: '#9FB2CB', fontSize: 12 }}>No matches.</div>}
            {results.map((r, i) => (
              <button key={`${r.kind}-${r.label}-${i}`}
                onClick={() => {
                  if (r.kind === 'nav') onNav(r);
                  if (r.kind === 'module') setExpandedModules((s) => ({ ...s, [r.id]: true }));
                  if (r.kind === 'topic') { setActiveTopic({ moduleId: r.moduleId, topicId: r.topicId }); setExpandedModules((s) => ({ ...s, [r.moduleId]: true })); }
                  setSearchQ('');
                }}
                style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: '#E8EEF6', padding: '5px 8px', fontSize: 12.5, cursor: 'pointer', borderRadius: 4, display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 10, color: C.gold, textTransform: 'uppercase', letterSpacing: '0.08em', minWidth: 46 }}>{r.kind}</span>
                <span>{r.label}</span>
              </button>
            ))}
          </div>
        )}

        {!results && (
          <>
            <ContentTree modules={modules} expandedModules={expandedModules}
              setExpandedModules={setExpandedModules}
              activeTopic={activeTopic} setActiveTopic={setActiveTopic} />
            {sections.map((section) => (
              <div key={section.title} style={{ marginBottom: 12 }}>
                <div style={{ padding: '8px 10px 4px', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#9FB2CB', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{section.title}</span>
                  {section.kicker && <span style={{ fontSize: 9, background: 'rgba(252,211,89,0.16)', color: C.gold, padding: '1px 6px', borderRadius: 3, letterSpacing: '0.05em' }}>{section.kicker}</span>}
                </div>
                {section.items.map((item) => (
                  <button key={item.label} onClick={() => onNav(item)}
                    data-testid={`console-nav-${item.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
                    title={item.hint}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 6, border: 'none', background: 'transparent', color: '#E8EEF6', borderLeft: '3px solid transparent', textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 500, marginBottom: 1 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <span style={{ width: 20, textAlign: 'center' }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </>
        )}
      </div>

      <div style={{ padding: '8px 12px', borderTop: '1px dashed #2A4A6E', fontSize: 10.5, color: '#9FB2CB', lineHeight: 1.55 }}>
        <div><kbd style={kbdStyle}>[</kbd> pin/unpin &nbsp; <kbd style={kbdStyle}>{KBD}</kbd>+<kbd style={kbdStyle}>K</kbd> search &nbsp; <kbd style={kbdStyle}>{KBD}</kbd>+<kbd style={kbdStyle}>J</kbd> tutor</div>
      </div>
    </aside>
  );
}

function ContentTree({ modules, expandedModules, setExpandedModules, activeTopic, setActiveTopic }) {
  return (
    <div style={{ marginBottom: 12 }} data-testid="drawer-content-tree">
      <div style={{ padding: '8px 10px 4px', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#9FB2CB', fontWeight: 700 }}>📚 Course Content</div>
      {modules.length === 0 && <div style={{ padding: '6px 10px', color: '#9FB2CB', fontSize: 12 }}>Loading modules…</div>}
      {modules.map((m) => {
        const expanded = !!expandedModules[m.id];
        return (
          <div key={m.id}>
            <button onClick={() => setExpandedModules({ ...expandedModules, [m.id]: !expanded })}
              data-testid={`content-module-${m.id}`}
              style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 10px', borderRadius: 6, border: 'none', background: 'transparent', color: '#E8EEF6', textAlign: 'left', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, lineHeight: 1.35 }}>
              <span style={{ width: 10, fontSize: 10, color: '#9FB2CB', marginTop: 3 }}>{expanded ? '▾' : '▸'}</span>
              <span style={{ color: C.gold, letterSpacing: '0.05em', minWidth: 24, fontSize: 10.5, marginTop: 2, fontFamily: '"Oswald", sans-serif' }}>{m.code}</span>
              <span style={{ flex: 1, wordBreak: 'break-word' }}>{m.name}</span>
              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: STATUS[m.status].bg, color: STATUS[m.status].fg, border: `1px solid ${STATUS[m.status].border}`, marginTop: 2, letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{m.status === 'Needs review' ? 'REVIEW' : m.status.toUpperCase()}</span>
            </button>
            {expanded && m.topics.map((t) => {
              const active = activeTopic.moduleId === m.id && activeTopic.topicId === t.id;
              return (
                <button key={t.id} onClick={() => setActiveTopic({ moduleId: m.id, topicId: t.id })}
                  data-testid={`content-topic-${t.id}`}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 38px', borderRadius: 6, border: 'none', background: active ? 'rgba(252,211,89,0.14)' : 'transparent', color: active ? C.gold : '#C7D3E3', borderLeft: `3px solid ${active ? C.gold : 'transparent'}`, textAlign: 'left', cursor: 'pointer', fontSize: 12 }}>
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span style={{ flex: 1 }}>{t.name}</span>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/* Helpers */
function cleanTitle(raw) {
  if (!raw) return '(untitled)';
  let t = String(raw).trim();
  t = t.replace(/^Module\s+\d+\s*:?\s*/i, '');
  t = t.replace(/^\(Weeks?[^)]*\)\s*/i, '');
  t = t.replace(/^Module\s+\d+\s*:?\s*/i, '');
  t = t.replace(/^\s*[:\-–]\s*/, '');
  return t.trim() || raw.trim();
}
function normStatus(s) {
  const n = String(s || '').toLowerCase();
  if (n === 'published') return 'Published';
  if (n === 'draft')     return 'Draft';
  return 'Needs review';
}
